import React from 'react';
import { useTranslation } from 'react-i18next';
import { LegacyJob } from '../types';
import JobRow from './JobRow';

type Job = LegacyJob;

interface EmptyStateProps {
  onClearFilters: () => void;
  recommendedJobs?: Job[];
  onSelectJob?: (job: Job) => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  onClearFilters,
  recommendedJobs = [],
  onSelectJob
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center py-16 sm:py-24 text-center px-6">
      {/* Icon */}
      <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
        <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {/* Title */}
      <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2 tracking-tight">
        {t('fullJobList.noResultsTitle')}
      </h3>

      {/* Description */}
      <p className="text-slate-500 max-w-sm mx-auto font-medium text-sm leading-relaxed mb-8">
        {t('fullJobList.noResultsDesc')}
      </p>

      {/* Clear Filters Button */}
      <button
        onClick={onClearFilters}
        className="mb-12 bg-slate-900 text-white px-8 py-3 rounded-xl font-bold text-sm hover:bg-blue-600 transition-all active:scale-95 cursor-pointer"
      >
        {t('fullJobList.clearFilters')}
      </button>

      {/* Recommended Jobs Section */}
      {recommendedJobs.length > 0 && (
        <div className="w-full max-w-4xl">
          <div className="flex items-center justify-center gap-2 mb-6">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h4 className="text-lg font-bold text-slate-900">
              {t('fullJobList.recommendedJobs') || '推荐职位'}
            </h4>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {recommendedJobs.map(job => (
              <JobRow key={job.id} job={job} onSelect={onSelectJob} />
            ))}
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-slate-700 mb-1">
            {t('fullJobList.tips.filter') || '减少筛选条件'}
          </p>
          <p className="text-xs text-slate-500">
            {t('fullJobList.tips.filterDesc') || '尝试减少或修改筛选条件'}
          </p>
        </div>

        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-slate-700 mb-1">
            {t('fullJobList.tips.search') || '尝试搜索关键词'}
          </p>
          <p className="text-xs text-slate-500">
            {t('fullJobList.tips.searchDesc') || '使用职位名称或部门搜索'}
          </p>
        </div>

        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-slate-700 mb-1">
            {t('fullJobList.tips.location') || '查看其他城市'}
          </p>
          <p className="text-xs text-slate-500">
            {t('fullJobList.tips.locationDesc') || '不同城市有不同职位'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmptyState;
